import * as React from "react";
import { useMyStore } from "store";
import {
  getFBC,
  getFBP,
  getKeitaroSubid,
  getPlayGameDomain,
  getPropellAdsUTMPrid,
  getRichAdsClickId,
  getTTCLID,
  getTTP,
} from "utils";

import {
  getSignupErrorMessage,
  isTurnstileRequiredError,
  NauErrorPayload,
} from "utils/authErrors";
import { getTurnstileSiteKey } from "utils/turnstile";
import { setAuthSession } from "utils/authSession";
import { flushEngagementThen } from "utils/dataLayer";
import { getDeviceFingerprint } from "utils/device";
import { trackRegister } from "hooks/useExperiment";

const DEFAULT_CURRENCY = process.env.REACT_APP_NAU_CURRENCY ?? "vno";
const NAU_TENANT_ID = process.env.REACT_APP_NAU_TENANT_ID;
const PHONE_COUNTRY_CODE = process.env.REACT_APP_PHONE_COUNTRY_CODE ?? "VN";

const buildSignupUrl = () => {
  const bootstrap = useMyStore.getState().bootstrap;
  return `https://${bootstrap?.apiDomain}/api/v1/signup`;
};

const getCookieValue = (name: string) => {
  if (typeof document === "undefined") {
    return null;
  }

  const pattern = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`);
  const match = document.cookie.match(pattern);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * `offerOverride` is for landings tied to one campaign. It wins over the query
 * string rather than filling in for it: those pages are reached by links we do
 * not control, so an `offer` someone appended to the URL must not retag them.
 */
export const buildSignupHeaders = (
  turnstileToken: string | null,
  offerOverride?: string,
) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "x-1net-channel": "web",
  };

  if (NAU_TENANT_ID) {
    headers["x-tenant-id"] = NAU_TENANT_ID;
  }

  headers["x-referer"] = window.location.origin;

  const fbp = getFBP();
  if (fbp) headers["x-fbp"] = fbp;

  const fbclid = getFBC();
  if (fbclid) headers["x-fbclid"] = fbclid;

  const ttp = getTTP();
  if (ttp) headers["x-ttp"] = ttp;

  const ttclid = getTTCLID();
  if (ttclid) headers["x-ttclid"] = ttclid;

  const richAdsClickId = getRichAdsClickId();
  if (richAdsClickId) headers["x-rich-ads-click-id"] = richAdsClickId;

  const propellAdsUTMPrid = getPropellAdsUTMPrid();
  if (propellAdsUTMPrid) headers["x-pro-pell-ads-utm-prid"] = propellAdsUTMPrid;

  const keitaroSubid = getKeitaroSubid();
  if (keitaroSubid) headers["x-keitaro-subid"] = keitaroSubid;

  if (turnstileToken) {
    headers["x-turnstile-response"] = turnstileToken;
  }

  const sitekey = getTurnstileSiteKey();
  if (sitekey) headers["x-turnstile-sitekey"] = sitekey;

  const q = new URLSearchParams(window?.location?.search);
  const utmSource = q.get("utm_source");
  if (utmSource) {
    headers["x-utm-source"] = decodeURIComponent(utmSource);
  }

  const adsid = q.get("a");
  if (adsid) {
    headers["x-adsid"] = decodeURIComponent(adsid);
  }

  const campaign = q.get("utm_campaign");
  if (campaign) {
    headers["x-utm-campaign"] = decodeURIComponent(campaign);
  }

  const medium = q.get("utm_medium");
  if (medium) {
    headers["x-utm-medium"] = decodeURIComponent(medium);
  }

  const term = q.get("utm_term");
  if (term) {
    headers["x-utm-term"] = decodeURIComponent(term);
  }

  const content = q.get("utm_content");
  if (content) {
    headers["x-utm-content"] = decodeURIComponent(content);
  }

  const aff = q.get("aff");
  if (aff) {
    headers["x-aff-code"] = aff;
  }

  const clickId = q.get("click_id");
  if (clickId) {
    headers["x-1net-click-id"] = decodeURIComponent(clickId);
  }
  const variantToken = q.get("v");
  if (variantToken) {
    headers["x-1net-variant"] = decodeURIComponent(variantToken);
  }
  const bonusCode = q.get("bonus");
  if (bonusCode) {
    headers["x-1net-bonus"] = decodeURIComponent(bonusCode);
  }
  const prid = q.get("utm_prid");
  if (prid) {
    headers["x-1net-prid"] = decodeURIComponent(prid);
  }
  const offer = offerOverride || q.get("offer");
  if (offer) {
    headers["x-1net-offer"] = offerOverride
      ? offerOverride
      : decodeURIComponent(offer);
  }
  const giftcode = q.get("giftcode");
  if (giftcode) {
    headers["x-1net-giftcode"] = decodeURIComponent(giftcode);
  }

  return headers;
};

type UseSignupFormOptions = {
  requireConfirmPassword?: boolean;
  formId?: string;
  /** Pins x-1net-offer for a landing that belongs to a single campaign. */
  offer?: string;
  onSuccess?: () => void;
  onTurnstileReset?: () => void;
};

type NauSignupSuccess = {
  token_info?: {
    user_id?: string;
    id_token?: string;
    access_token?: string;
  };
};

export const useSignupForm = ({
  requireConfirmPassword = true,
  formId = "register_popup",
  offer,
  onSuccess,
  onTurnstileReset,
}: UseSignupFormOptions = {}) => {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(
    null,
  );
  const [turnstileRequired, setTurnstileRequired] = React.useState(false);
  const isSubmittingRef = React.useRef(false);
  const fingerprintPrefetched = React.useRef(false);

  const usernameError = submitted && username.trim() === "";
  const passwordError = submitted && password.trim() === "";

  const clearTurnstileGate = React.useCallback(() => {
    setTurnstileRequired(false);
    setTurnstileToken(null);
    onTurnstileReset?.();
  }, [onTurnstileReset]);

  const submitSignup = React.useCallback(
    async (token: string | null) => {
      if (isSubmittingRef.current) {
        return;
      }

      const trimmedUsername = username.trim();
      const trimmedPassword = password.trim();
      if (!trimmedUsername || !trimmedPassword) {
        return;
      }

      const signupUrl = buildSignupUrl();
      if (!signupUrl) {
        setApiError("Thiếu cấu hình máy chủ, vui lòng liên hệ quản trị.");
        return;
      }

      isSubmittingRef.current = true;
      setIsSubmitting(true);
      setApiError(null);

      const deviceId = await getDeviceFingerprint();

      try {
        const response = await fetch(signupUrl, {
          method: "POST",
          headers: buildSignupHeaders(token, offer),
          body: JSON.stringify({
            my_id: {
              username: trimmedUsername,
              password: trimmedPassword,
              confirm_password: trimmedPassword,
            },
            currency: DEFAULT_CURRENCY,
            originCountry: PHONE_COUNTRY_CODE,
            device_id: deviceId,
            device_name: navigator.userAgent,
          }),
        });

        const responseText = await response.text();
        if (!response.ok) {
          let errorPayload: NauErrorPayload | null = null;
          try {
            errorPayload = responseText
              ? (JSON.parse(responseText) as NauErrorPayload)
              : null;
          } catch {
            errorPayload = null;
          }

          if (isTurnstileRequiredError(response.status, errorPayload)) {
            setTurnstileRequired(true);
            setApiError(null);
            setTurnstileToken(null);
            onTurnstileReset?.();
            return;
          }

          setApiError(getSignupErrorMessage(response.status, errorPayload));
          setTurnstileRequired(false);
          setTurnstileToken(null);
          return;
        }

        let payload: NauSignupSuccess | null = null;
        try {
          payload = responseText
            ? (JSON.parse(responseText) as NauSignupSuccess)
            : null;
        } catch {
          payload = null;
        }

        const idToken = payload?.token_info?.id_token;
        const userId = payload?.token_info?.user_id;

        setAuthSession({ userId });

        if (userId && useMyStore.getState().experimentTheme) {
          trackRegister(userId);
        }

        if (onSuccess) {
          onSuccess();
        }

        await flushEngagementThen(getPlayGameDomain({ idToken }), {
          event: "lp_form_submit",
          form_id: formId,
        });
      } catch {
        setApiError("Không thể kết nối máy chủ, vui lòng thử lại.");
        setTurnstileRequired(false);
        setTurnstileToken(null);
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [formId, offer, onSuccess, password, username, onTurnstileReset],
  );

  const reset = React.useCallback(() => {
    setSubmitted(false);
    setUsername("");
    setPassword("");
    setApiError(null);
    setIsSubmitting(false);
    isSubmittingRef.current = false;
    setTurnstileRequired(false);
    setTurnstileToken(null);
    onTurnstileReset?.();
  }, [onTurnstileReset]);

  const handleFieldChange = React.useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setter(event.target.value);
        if (apiError) {
          setApiError(null);
        }
        if (turnstileRequired) {
          clearTurnstileGate();
        }
      },
    [apiError, turnstileRequired, clearTurnstileGate],
  );

  const handlePasswordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!fingerprintPrefetched.current) {
        fingerprintPrefetched.current = true;
        void getDeviceFingerprint();
      }
      setPassword(event.target.value);
      if (apiError) {
        setApiError(null);
      }
      if (turnstileRequired) {
        clearTurnstileGate();
      }
    },
    [apiError, turnstileRequired, clearTurnstileGate],
  );

  const handleSubmit = React.useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      setSubmitted(true);

      const trimmedUsername = username.trim();
      const trimmedPassword = password.trim();
      if (!trimmedUsername || !trimmedPassword) {
        return;
      }

      await submitSignup(turnstileRequired ? turnstileToken : null);
    },
    [username, password, turnstileRequired, turnstileToken, submitSignup],
  );

  const handleTurnstileTokenChange = React.useCallback(
    (token: string | null) => {
      setTurnstileToken(token);
      if (turnstileRequired && token) {
        void submitSignup(token);
      }
    },
    [turnstileRequired, submitSignup],
  );

  return {
    username,
    password,
    isSubmitting,
    apiError,
    usernameError,
    passwordError,
    turnstileRequired,
    handleSubmit,
    handleUsernameChange: handleFieldChange(setUsername),
    handlePasswordChange,
    handleTurnstileTokenChange,
    reset,
    setSubmitted,
  };
};
